import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ConfigConstants } from '../../config/config-constants';
import { ODataModel } from '../../interfaces/odata-model.interface';
import { DropdownModel } from '../../models/complex/dropdown.model';
import { BooleanModelOptions } from '../../models/options/boolean-model-options';
import { DateModelOptions } from '../../models/options/date-model-options';
import { DropdownModelOptions } from '../../models/options/dropdown-model-options';
import { DurationModelOptions } from '../../models/options/duration-model-options';
import { NumberModelOptions } from '../../models/options/number-model-options';
import { RatingModelOptions } from '../../models/options/rating-model-options';
import { TimeModelOptions } from '../../models/options/time-model-options';
import { BooleanModel } from '../../models/primitive/boolean.model';
import { DateModel } from '../../models/primitive/date.model';
import { DurationModel } from '../../models/primitive/duration.model';
import { InternalInfoMultilineTextModel } from '../../models/primitive/internal-info-multiline-text.model';
import { MultilineTextModel } from '../../models/primitive/multiline-text.model';
import { NumberModel } from '../../models/primitive/number.model';
import { RatingModel } from '../../models/primitive/rating.model';
import { TextModel } from '../../models/primitive/text.model';
import { TimeModel } from '../../models/primitive/time.model';
import { InputFieldPropertiesService } from '../input-field-properties/input-field-properties.service';

@Injectable({
    providedIn: 'root',
})
export class ModelOptionsLoaderService {
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly inputFieldPropertiesService: InputFieldPropertiesService,
    ) {}

    public load(
        model: ODataModel,
        screenName: string,
        fieldName: string,
        process = ConfigConstants.PROCESS_INDICATOR.INVIVO,
    ): void {
        // Copy validation rules from input field properties according to model type
        const inputFieldPropertyModel = this.inputFieldPropertiesService.getInputFieldProperties(
            fieldName,
            screenName,
            process,
        );

        if (!inputFieldPropertyModel) {
            this.log.warn(`Could not load properties for field '${fieldName}' on screen '${screenName}'!`);
            return;
        }

        if (
            model instanceof TextModel ||
            model instanceof MultilineTextModel ||
            model instanceof InternalInfoMultilineTextModel
        ) {
            const minLength = inputFieldPropertyModel.minLength;
            if (!isNaN(minLength)) {
                model.options = {
                    ...model.options,
                    minLength,
                };
            }
            const maxLength = inputFieldPropertyModel.maxLength;
            if (!isNaN(maxLength)) {
                model.options = {
                    ...model.options,
                    maxLength: maxLength > 0 ? maxLength : Infinity,
                };
            }
            const required = inputFieldPropertyModel.mandatory;
            if (required) {
                model.options = {
                    ...model.options,
                    required,
                };
            }
            const readonly = inputFieldPropertyModel.readonly;
            if (readonly) {
                model.options = {
                    ...model.options,
                    readonly,
                };
            }
            const pattern = inputFieldPropertyModel.pattern;
            if (pattern) {
                model.options = {
                    ...model.options,
                    pattern,
                };
            }
            const patternScan = inputFieldPropertyModel.patternScan;
            if (patternScan) {
                model.options = {
                    ...model.options,
                    patternScan,
                };
            }
            const scannable = inputFieldPropertyModel.scannable;
            if (scannable) {
                model.options = {
                    ...model.options,
                    scannable: scannable,
                };
            }
        } else if (model instanceof NumberModel) {
            const minLength = inputFieldPropertyModel.minLength;
            if (!isNaN(minLength)) {
                model.options = new NumberModelOptions({ ...model.options, minLength });
            }
            const maxLength = inputFieldPropertyModel.maxLength;
            if (!isNaN(maxLength)) {
                model.options = new NumberModelOptions({ ...model.options, maxLength });
            }
            const minValue = inputFieldPropertyModel.minValue;
            if (!isNaN(minValue)) {
                model.options = new NumberModelOptions({ ...model.options, minValue });
            }
            const maxValue = inputFieldPropertyModel.maxValue;
            if (!isNaN(maxValue)) {
                model.options = new NumberModelOptions({ ...model.options, maxValue });
            }
            const required = inputFieldPropertyModel.mandatory;
            if (required) {
                model.options = new NumberModelOptions({ ...model.options, required });
            }
            const readonly = inputFieldPropertyModel.readonly;
            if (readonly) {
                model.options = new NumberModelOptions({ ...model.options, readonly });
            }
            const pattern = inputFieldPropertyModel.pattern;
            if (pattern) {
                model.options = new NumberModelOptions({ ...model.options, pattern });
            }
            const patternScan = inputFieldPropertyModel.patternScan;
            if (patternScan) {
                model.options = new NumberModelOptions({ ...model.options, patternScan });
            }
            const scannable = inputFieldPropertyModel.scannable;
            if (scannable) {
                model.options = new NumberModelOptions({ ...model.options, scannable });
            }
        } else if (model instanceof DropdownModel) {
            const required = inputFieldPropertyModel.mandatory;
            if (required) {
                model.options = new DropdownModelOptions({
                    ...model.options,
                    required,
                });
            }
            const minLength = inputFieldPropertyModel.minLength;
            if (!isNaN(minLength)) {
                model.options = {
                    ...model.options,
                    minLength,
                };
            }
            const maxLength = inputFieldPropertyModel.maxLength;
            if (!isNaN(maxLength)) {
                model.options = {
                    ...model.options,
                    maxLength: maxLength > 0 ? maxLength : Infinity,
                };
            }
            const patternScan = inputFieldPropertyModel.patternScan;
            if (patternScan) {
                model.options = new DropdownModelOptions({
                    ...model.options,
                    patternScan,
                });
            }
            const scannable = inputFieldPropertyModel.scannable;
            if (scannable) {
                model.options = new DropdownModelOptions({
                    ...model.options,
                    scannable,
                });
            }
        } else if (model instanceof DateModel) {
            const readonly = inputFieldPropertyModel.readonly;
            if (readonly) {
                model.options = new DateModelOptions({
                    ...model.options,
                    readonly,
                });
            }
        } else if (model instanceof TimeModel) {
            const readonly = inputFieldPropertyModel.readonly;
            if (readonly) {
                model.options = new TimeModelOptions({
                    ...model.options,
                    readonly,
                });
            }
        } else if (model instanceof DurationModel) {
            const required = inputFieldPropertyModel.mandatory;
            if (required) {
                model.options = new DurationModelOptions({
                    ...model.options,
                    required,
                });
            }
            const readonly = inputFieldPropertyModel.readonly;
            if (readonly) {
                model.options = new DurationModelOptions({
                    ...model.options,
                    readonly,
                });
            }
        } else if (model instanceof BooleanModel) {
            const defaultValue = inputFieldPropertyModel.defaultValue;
            if (defaultValue) {
                model.options = new BooleanModelOptions({
                    ...model.options,
                    defaultValue: !!defaultValue,
                });
                model.value = model.options.defaultValue;
            }
        } else if (model instanceof RatingModel) {
            const readonly = inputFieldPropertyModel.readonly;
            const defaultValue = inputFieldPropertyModel.defaultValue;
            if (readonly) {
                model.options = new RatingModelOptions({
                    ...model.options,
                    readonly,
                });
            }
            if (defaultValue) {
                model.value = defaultValue;
            }
        }
    }
}
